/** @format */

import {LockClosedIcon} from '@heroicons/react/solid'
import React, {
  ChangeEventHandler,
  HTMLInputTypeAttribute,
  ReactNode,
} from 'react'

import ActionInput from '@src/components/tailwind/ActionInput'
import LoaderButton from '@src/components/tailwind/LoaderButton'

import {PluginKind} from '@src/types/Plugin'

import {AddedByActionsText} from './AddedByActionsText'

type EditFieldInputProps = CommonProps & {
  addedBy?: Set<PluginKind>
  // Intended for error text or help text to display in EditableMode
  // such as "Cannot rename the field to <newFieldName> because it's already exists."
  editableModeDescription?: ReactNode
} & EditableModeProps

// EditFieldInput conditionally renders ActionInput in either ReadOnlyMode or
// EditableMode based on addedBy props i.e. whether there are
// any validation rules added by actions.
//
// In the ReadOnlyMode, the input value cannot be changed and saving is disabled.
//
// In the EditableMode, the input value can be changed. Saving is enabled
// unless saveDisabled props is true.
export function EditFieldInput(props: EditFieldInputProps) {
  const {
    type = 'text',
    label,
    value,
    addedBy,
    editableModeDescription,
    ...editableModeProps
  } = props

  const commonProps = {type, label, value}

  return addedBy ? (
    <ReadOnlyMode {...commonProps} addedBy={addedBy} />
  ) : (
    <EditableMode
      {...commonProps}
      {...editableModeProps}
      description={editableModeDescription}
    />
  )
}

type CommonProps = {
  type?: HTMLInputTypeAttribute
  label: string
  value: string
}

type ReadOnlyModeProps = {
  addedBy: Set<PluginKind>
}

function ReadOnlyMode(props: CommonProps & ReadOnlyModeProps) {
  const {addedBy, ...inputProps} = props
  return (
    <ActionInput
      {...inputProps}
      readOnly
      description={
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <LockClosedIcon className="h-4 w-4 shrink-0" />
          <AddedByActionsText addedBy={addedBy} />
        </div>
      }
    />
  )
}

type EditableModeProps = {
  description?: ReactNode
  max?: number | string
  min?: number | string
  onChange: ChangeEventHandler<HTMLInputElement>
  onSaveButtonClick: () => void
  saveDisabled?: boolean
}

function EditableMode(props: CommonProps & EditableModeProps) {
  const {onSaveButtonClick, saveDisabled = false, ...inputProps} = props
  return (
    <ActionInput {...inputProps}>
      <LoaderButton disabled={saveDisabled} onClick={onSaveButtonClick}>
        Save
      </LoaderButton>
    </ActionInput>
  )
}
